import React from 'react';
import {ViewTicketGroup} from '../../components/TicketGroup/View';
import {useParams} from 'react-router-dom';
import {
  useGetListTicketGroup,
  useGetTicketGroup,
  useTicketsActions,
  useSendTicket,
  useMultipleUpdate,
  useSetTicketState,
  useAddGuests,
  useMultipleSendbyEmails,
  useReSync,
  useUpdateGuest,
  useUploadGuestEmails,
  useResetGroup,
} from '../../hooks/tickets';
import {InternalTicketFooterContainer, TicketFooterContainer} from '../Layout/Footer';
import {getPermissions} from '../../helpers/roles';
import {openNotifError} from '../../components/Dashboard';
import {CodeAccessTypes} from '../../ui-kit/helpers/codes';
import {Onboarding} from './Onboarding';
import {useHelpModal} from '../../hooks/onboarding';

export const ViewTicket = () => {
  const {id} = useParams();
  const {
    ticketGroup,
    loading: ticketLoading,
    refetch: groupRefetch,
    metrics,
    groupedTicketTypes,
  } = useGetTicketGroup(id);
  const {
    tableData,
    loading: listLoading,
    refetch,
    usedTickets,
    emailsMap,
    searchTickets,
  } = useGetListTicketGroup(id, metrics);
  const handleRefetch = () => {
    groupRefetch();
    refetch();
  };
  const {onDelete, onUpdate} = useTicketsActions(id, handleRefetch, openNotifError);
  const {onSend} = useSendTicket(id, handleRefetch);
  const addGuestData = useAddGuests(metrics, usedTickets, id, handleRefetch);
  const {onUpdateGuest} = useUpdateGuest(id, refetch, openNotifError);
  const {reSync} = useReSync(ticketGroup, handleRefetch, openNotifError);
  const ticketState = useSetTicketState();
  const {updateAll} = useMultipleUpdate({groupId: id});
  const {sendMultiple, loadingSend} = useMultipleSendbyEmails(id, handleRefetch, openNotifError);
  // Guests that actually have at least one unsent ticket assigned. A guest with
  // no tickets (e.g. an uploaded email left at 0) is reported as "sent" here and
  // therefore excluded.
  const notSentGuestIds = tableData
    .filter((row) => !!row.id && row.id !== 'add' && row.actions?.ticketStatus === CodeAccessTypes.notsent)
    .map((row) => row.id as string);
  const onBulk = async () => {
    // Persist inline email/name edits first.
    await updateAll(ticketState.state);
    // The "send all not-sent" endpoint throws when any not-sent guest has no
    // tickets assigned, which aborts the whole batch server-side. Send
    // explicitly only to guests that have unsent tickets so those empty guests
    // never enter the loop.
    if (!notSentGuestIds.length) {
      return {res: false, error: 'No tickets are ready to send. Assign tickets to a guest and click Save first.'};
    }
    const ok = await sendMultiple(notSentGuestIds);
    if (!ok) return {res: false, error: 'Failed to send tickets. Please try again.'};
    document.location.reload();
    return {res: true, error: ''};
  };
  const {isAssociate} = getPermissions();
  const canAddEdit = !isAssociate;
  const modals = useHelpModal();
  const {upload: uploadEmails} = useUploadGuestEmails(id ? [id] : [], handleRefetch);
  const handleReset = useResetGroup(ticketGroup, handleRefetch);
  return (
    <>
      <Onboarding id={id} />
      <ViewTicketGroup
        ticketGroup={ticketGroup}
        ticketLoading={ticketLoading}
        tableData={tableData}
        onDelete={onDelete}
        onUpdate={onUpdate}
        onSendTicket={onSend}
        listLoading={listLoading}
        ticketState={ticketState}
        groupRefetch={groupRefetch}
        addGuestData={addGuestData}
        sendMultiple={sendMultiple}
        loadingSend={loadingSend}
        canAddEdit={canAddEdit}
        fullRefetch={handleRefetch}
        onBulk={onBulk}
        viewMetrics={metrics}
        usedTickets={usedTickets}
        emailsMap={emailsMap}
        onUpdateGuest={onUpdateGuest}
        reSync={reSync}
        searchTickets={searchTickets}
        onUploadEmails={uploadEmails}
        modals={modals}
        groupedTicketTypes={groupedTicketTypes}
        handleReset={handleReset}
      />
      {ticketGroup?.id ? (
        ticketGroup?.groupType === 'external' ? (
          <TicketFooterContainer onSuccess={handleRefetch} />
        ) : (
          <InternalTicketFooterContainer onSuccess={handleRefetch} onBulk={onBulk} group={ticketGroup} />
        )
      ) : (
        <></>
      )}
    </>
  );
};
